/**
 * mensajesInscripcionClasesFijas.js
 *
 * Sub-fase 6.C · Mapeo de códigos backend (Sub-fase 6.A) y códigos del
 * helper construirPayloadTurnoFijo (Sub-fase 6.B) a mensajes específicos
 * y accionables para mostrar al editor en el modal de inscripción.
 *
 * NO importa React, NO depende de toast · pura traducción de error a string.
 *
 * Códigos cubiertos:
 *   CUOTA_HORAS_SEMANA_NO_CONFIGURADA  (6.A)
 *   CUOTA_ID_REQUERIDO                 (6.A)
 *   CUOTA_NO_EXISTE                    (6.A)
 *   CUOTA_PRECIO_NO_CONFIGURADO        (6.A)
 *   TURNO_NO_COINCIDE_CUOTA            (6.A · alineado con 6.B)
 *   TURNO_INCOMPLETO                   (6.A · alineado con 6.B)
 *   TURNO_DUPLICADO                    (6.A · alineado con 6.B)
 *   TURNO_DIA_REPETIDO                 (6.B · sólo si modo blocking)
 *   TURNO_FIJO_FORMATO_INVALIDO        (6.A)
 *   PAYLOAD_INVALIDO                   (6.A · genérico backend)
 *   FECHA_INICIO_INVALIDA              (preexistente)
 *   SEDE_INVALIDA / SEDE_NO_COINCIDE   (preexistente)
 *   CLIENTE_*                          (preexistente)
 *   AFORO_COMPLETO / AFORO_SUPERADO    (preexistente)
 *   DATOS_BANCARIOS_INSUFICIENTES      (preexistente)
 *   BECADO_FUERA_ALCANCE_*             (preexistente)
 *   MOTIVO_*                           (preexistente)
 *   POSTCONDICION_FALLIDA              (preexistente)
 */

/**
 * mensajeUiPorCodigo · función pura.
 *
 * @param {string} code   · código tipado del backend o del helper local
 * @param {object} detail · objeto detail estructurado (puede venir como string)
 * @returns {string}      · mensaje legible al editor
 */
export function mensajeUiPorCodigo(code, detail) {
  // Normalizar detail para tolerar strings o undefined
  const d = (detail && typeof detail === "object") ? detail : {};

  switch (code) {
    // ── CUOTA · 6.A ────────────────────────────────────────────────────
    case "CUOTA_HORAS_SEMANA_NO_CONFIGURADA":
      return "La membresía seleccionada no tiene horas semanales configuradas. " +
             "Configura horas_semana en el catálogo o elige otra membresía.";

    case "CUOTA_ID_REQUERIDO":
      return "Selecciona una membresía/cuota antes de inscribir.";

    case "CUOTA_NO_EXISTE":
      return "La membresía seleccionada ya no existe en el catálogo. " +
             "Refresca el listado y vuelve a elegir.";

    case "CUOTA_PRECIO_NO_CONFIGURADO":
      return "La membresía no tiene precio configurado · no es asignable " +
             "hasta que se complete.";

    // ── TURNOS · 6.A + 6.B ─────────────────────────────────────────────
    case "TURNO_NO_COINCIDE_CUOTA":
      if (typeof d.turnos_requeridos === "number"
          && typeof d.turnos_recibidos === "number") {
        return `La membresía seleccionada requiere ${d.turnos_requeridos} ` +
               `horarios semanales y has rellenado ${d.turnos_recibidos}.`;
      }
      return d.razon || "El número de horarios no coincide con la membresía.";

    case "TURNO_INCOMPLETO": {
      const lista = Array.isArray(d.turnos_incompletos) ? d.turnos_incompletos : [];
      if (lista.length === 0) {
        return d.razon || "Hay turnos incompletos · completa día y hora.";
      }
      const plural = lista.length === 1 ? "el turno" : "los turnos";
      return `Completa el día y la hora de ${plural} ${lista.join(", ")}.`;
    }

    case "TURNO_DUPLICADO": {
      const repetidos = Array.isArray(d.repetidos) ? d.repetidos : [];
      if (repetidos.length === 0) {
        return d.razon || "Hay turnos duplicados · cada horario debe ser único.";
      }
      const muestra = repetidos
        .map((r) => `${r.dia} ${r.hora}`)
        .join(", ");
      return `No puedes repetir el mismo horario: ${muestra}.`;
    }

    case "TURNO_DIA_REPETIDO": {
      const dias = Array.isArray(d.dias_repetidos) ? d.dias_repetidos : [];
      if (dias.length === 0) {
        return d.razon || "Hay días repetidos en los turnos.";
      }
      const muestra = dias
        .map((r) => `${r.dia}${r.veces ? ` (x${r.veces})` : ""}`)
        .join(", ");
      return `No puedes repetir el mismo día: ${muestra}.`;
    }

    case "TURNO_FIJO_FORMATO_INVALIDO":
      return d.razon
        || "Formato de horario inválido · revisa día y hora.";

    // ── PAYLOAD · estructura ──────────────────────────────────────────
    case "PAYLOAD_INVALIDO": {
      if (Array.isArray(d.faltan_campos) && d.faltan_campos.length > 0) {
        return `Faltan campos obligatorios: ${d.faltan_campos.join(", ")}.`;
      }
      return d.razon || "Payload inválido · revisa los campos del formulario.";
    }

    // ── CLIENTE / FECHAS / SEDE · preexistentes ───────────────────────
    case "CLIENTE_NO_EXISTE":
      return "Cliente no encontrado · recarga la ficha.";

    case "CLIENTE_EN_IS_ACTIVA":
      return "Este cliente tiene una incidencia de salud activa. " +
             "Cierra la IS antes de inscribirlo.";

    case "CLIENTE_YA_INSCRITO":
      return "El cliente ya está inscrito en clases fijas.";

    case "FECHA_INICIO_INVALIDA":
      return d.razon
        || "La fecha de inicio no es válida (formato YYYY-MM-DD, dentro del año en curso).";

    case "SEDE_INVALIDA":
      return "La sede seleccionada no existe en el sistema.";

    case "SEDE_NO_COINCIDE":
      return "La sede seleccionada no coincide con la del cliente. " +
             "Cambia la sede en la ficha primero.";

    // ── AFORO ─────────────────────────────────────────────────────────
    case "AFORO_COMPLETO":
    case "AFORO_SUPERADO":
      if (d.dia && d.hora && d.sede) {
        return `🛑 Aforo superado en ${d.dia} ${d.hora} (${d.sede}). ` +
               `Elige otro horario.`;
      }
      return "Uno de los turnos seleccionados está al límite de aforo. " +
             "Elige otro horario.";

    // ── FORMA DE PAGO ─────────────────────────────────────────────────
    case "FORMA_PAGO_INVALIDA":
      return "Forma de pago no permitida.";

    case "BECADO_FUERA_ALCANCE":
    case "BECADO_FUERA_ALCANCE_BLOQUE_1":
      return "Becado no disponible en esta versión · usa otra forma de pago.";

    case "DATOS_BANCARIOS_INSUFICIENTES": {
      if (Array.isArray(d.faltan) && d.faltan.length > 0) {
        return "Para cobrar clases fijas por domiciliación, completa en "
             + "Datos principales: " + d.faltan.join(", ") + ".";
      }
      return "Para cobrar clases fijas por domiciliación, completa en "
           + "Datos principales: IBAN, titular de la cuenta y DNI del titular/pagador.";
    }

    // ── CUOTA INSCRIPCION ─────────────────────────────────────────────
    case "MOTIVO_EXENCION_OBLIGATORIO":
      return "Indica el motivo de exención (importe = 0).";

    case "MOTIVO_DESCUENTO_OBLIGATORIO":
      return "Indica el motivo del descuento (importe < 150€).";

    case "CUOTA_INSCRIPCION_SUPERIOR_NO_PERMITIDA_EN_BLOQUE_1":
      return "El importe de la cuota de inscripción no puede superar 150€.";

    // ── ATÓMICO BACKEND ──────────────────────────────────────────────
    case "SUSCRIPCION_NO_CREADA":
      return "No se pudo crear la suscripción · avisa al equipo técnico.";

    case "COBROS_NO_GENERADOS":
      return "No se generaron los cobros del año · operación revertida automáticamente.";

    case "POSTCONDICION_FALLIDA":
      return "La inscripción no se completó correctamente · operación revertida. " +
             "Revisa el log.";

    // ── Fallback ──────────────────────────────────────────────────────
    default:
      if (d && typeof d.razon === "string" && d.razon.trim()) {
        return d.razon;
      }
      if (typeof detail === "string" && detail.trim()) {
        return detail;
      }
      return code
        ? `Error: ${code}`
        : "Error desconocido al procesar la inscripción.";
  }
}


// ──────────────────────────────────────────────────────────────────────────
// Sub-fase 7 · validación cruzada SEPA pre-bloqueo
// (Re-alineada con regla legal Ann 29/04/2026)
// ──────────────────────────────────────────────────────────────────────────

/**
 * mensajeFaltaSepaParaInscripcion · pura.
 *
 * Regla canónica (Ann 29/04/2026 · ver backend services/pdf_legal.py
 * _resolver_deudor_sepa): el deudor SEPA por defecto es el propio cliente.
 * sepa_titular_cuenta / sepa_titular_dni son OVERRIDE LEGACY opcional · si
 * están vacíos se DERIVA de nombre+apellidos / dni del cliente.
 *
 * Tres ramas:
 *   1. tiene_pagador_alternativo=True → exigir pagador_*.
 *   2. menor sin pagador alternativo → exigir tutor (la UI auto-fuerza el flag
 *      al cargar; esta rama queda como red de seguridad si el flag se quita).
 *   3. adulto sin pagador alternativo (CASO POR DEFECTO):
 *      - aceptar legacy sepa_titular_* si están poblados.
 *      - si no, derivar titular de nombre+primer_apellido y DNI de
 *        sepa_titular_dni || dni.
 *
 * @param {object} client    · objeto cliente (lectura · NO se muta)
 * @param {object} opts
 * @param {boolean} opts.isMenor       · true si el cliente es menor
 * @param {function} opts.validarIBAN  · validador IBAN inyectado (opcional)
 * @param {function} opts.validarDNI   · validador DNI inyectado (opcional)
 * @returns {string|null}              · mensaje al editor o null si OK
 */
export function mensajeFaltaSepaParaInscripcion(client, opts = {}) {
  const c = client || {};
  const isMenor = !!opts.isMenor;
  const validarIBAN = typeof opts.validarIBAN === "function"
    ? opts.validarIBAN
    : (s) => typeof s === "string" && s.trim().length >= 15;
  const validarDNI = typeof opts.validarDNI === "function"
    ? opts.validarDNI
    : (s) => typeof s === "string" && s.trim().length >= 8;

  const faltan = [];

  // IBAN siempre obligatorio
  if (!validarIBAN(c.sepa_iban)) faltan.push("IBAN válido");

  if (c.tiene_pagador_alternativo) {
    // ── Rama 1 · pagador alternativo ────────────────────────────────
    if (!c.pagador_nombre || !c.pagador_apellidos) {
      faltan.push("nombre y apellidos del pagador");
    }
    if (!validarDNI(c.pagador_dni)) faltan.push("DNI del pagador");
    if (!c.pagador_relacion || !String(c.pagador_relacion).trim()) {
      faltan.push("relación del pagador (padre/madre/tutor)");
    }
  } else if (isMenor) {
    // ── Rama 2 · menor sin pagador alternativo ──────────────────────
    if (!c.tutor_nombre) faltan.push("nombre del tutor");
    if (!validarDNI(c.tutor_dni)) faltan.push("DNI del tutor");
    if (!c.tutor_relacion || !String(c.tutor_relacion).trim()) {
      faltan.push("relación del tutor (padre/madre/tutor)");
    }
  } else {
    // ── Rama 3 · adulto sin pagador alternativo (CASO POR DEFECTO) ──
    // sepa_titular_cuenta / sepa_titular_dni son override legacy opcional.
    // Si están poblados se respetan. Si no, se deriva del cliente.
    const titularLegacy = (c.sepa_titular_cuenta || "").trim();
    const dniLegacy = (c.sepa_titular_dni || "").trim();

    if (!titularLegacy) {
      // Derivar titular de nombre+primer_apellido del cliente
      if (!c.nombre || !String(c.nombre).trim()) {
        faltan.push("nombre del cliente");
      }
      if (!c.primer_apellido || !String(c.primer_apellido).trim()) {
        faltan.push("primer apellido del cliente");
      }
    }

    // DNI titular: legacy override → cliente.dni como fallback
    if (!validarDNI(dniLegacy || c.dni)) {
      faltan.push("DNI del cliente");
    }
  }

  if (faltan.length === 0) return null;

  return "Para cobrar clases fijas por domiciliación, completa en Datos "
       + "principales: " + faltan.join(", ") + ".";
}
